<template>
  <v-menu ref="menu" offset-y :close-on-content-click="false">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        plain
        text
        class="hyp-text-menu-btn"
        :class="menuClass"
      >
        <span v-if="title">{{ title }}:</span>
        <span v-if="activeItemLabel">{{ activeItemLabel }}</span>
        <v-icon right>
          mdi-menu-down
        </v-icon>
      </v-btn>
      <span class="daterange-subtext" v-if="showActiveItemRange">
        {{ formatDateRangeLabel(activeRange) }}</span
      >
    </template>
    <v-container class="hyp-date-range-filters-container">
      <v-select
        v-model="selectedItem"
        item-text="value.label"
        :items="timeRangeItems"
        label="Time Period (UTC)"
        hide-details
        dense
        :menu-props="{
          closeOnClick: true,
          closeOnContentClick: true,
          maxHeight: '100%',
        }"
      >
        <template v-slot:item="{ item }">
          <v-list-item ripple @mousedown.prevent @click="handleClick(item)">
            <v-list-item-content>
              <v-list-item-title>
                {{ item.value.label }}
                <span class="daterange-subtext ml-3">
                  {{ formatDateRangeLabel(toDateRange(item.value)) }}
                </span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-select>
      <div v-if="selectedItem.useDatePicker" class="mt-5">
        <DateRangePicker
          size="small"
          class="mt-5"
          :start="customStartDate"
          :end="customEndDate"
          :min="minStartDate"
          :max="maxStartDate"
          @change="handleChangeCustomDates"
        ></DateRangePicker>
      </div>
      <div v-if="compare">
        <v-select
          v-model="selectedCompare"
          item-text="value.label"
          :items="compareItems"
          label="Compare To"
          hide-details
          dense
          :menu-props="{ closeOnClick: true, closeOnContentClick: true }"
          class="mt-8"
        ></v-select>
        <div v-if="selectedCompare.useDatePicker" class="mt-5">
          <DateRangePicker
            size="small"
            class="mt-5"
            :start="customCompareStartDate"
            :end="customCompareEndDate"
            :min="minCompareDate"
            @change="handleChangeCustomCompare"
          ></DateRangePicker>
        </div>
      </div>
      <div class="mt-3 text-center">
        <v-btn text class="default btn pa-4" @click="handleApply()"
          >Apply
        </v-btn>
        <v-btn text class="default btn pa-4" @click="handleCancel()"
          >Cancel
        </v-btn>
        <v-btn text class="default btn pa-4" @click="handleReset()"
          >Reset
        </v-btn>
      </div>
    </v-container>
  </v-menu>
</template>

<script>
import {
  DATE_FORMAT,
  getFormattedDate,
  getRelativeDate,
} from "@/common/date.utilities";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const EVENTS = {
  INPUT: "input",
  CHANGE: "change",
};

const defaultTimeOptions = [
  {
    divider: true,
  },
  {
    value: {
      label: "Custom Date Range",
      useDatePicker: true,
      isRelative: false,
    },
  },
];

const compareItems = [
  {
    value: {
      label: "Previous Period",
      useDatePicker: false,
    },
  },
  {
    value: {
      label: "Custom Period",
      useDatePicker: true,
    },
  },
];

export default {
  name: "DateRangeSelector",
  components: {
    DateRangePicker: () => import("@/components/common/DateRangePicker.vue"),
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    value: {
      type: Object,
      default: () => this.items[0],
    },
    customRange: {
      type: Object,
      default: () => ({
        start: null,
        end: null,
      }),
    },
    customCompareRange: {
      type: Object,
      default: () => ({
        start: null,
        end: null,
      }),
    },
    compare: {
      type: Boolean,
      default: false,
    },
    defaultContent: {
      type: Boolean,
      default: false,
    },
    menuClass: {
      type: String,
      default: "pull-left",
    },
    dateRangeMaxDateBack: {
      type: String,
    },
    dateRangeMaxDate: {
      type: String,
    },
    showActiveItemRange: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    const range = this.initRange(this.value);
    return {
      timeRangeItems: [...this.items, ...defaultTimeOptions],
      compareItems: [...compareItems],
      activeItem: this.value,
      activeRange: range,
      selectedItem: this.value,
      selectedRange: range,
      initialValue: this.value,
      selectedCompare: compareItems[0],
      activeCompare: compareItems[0],
      selectedCompareRange: this.getPrevPeriod(range),
      activeCompareRange: this.getPrevPeriod(range),
    };
  },
  async mounted() {
    if (!this.defaultContent) {
      this.emitChangeAction();
    }
  },
  computed: {
    activeItemLabel: function () {
      return this.activeItem.label;
    },
    customStartDate: function () {
      return this.selectedRange?.start?.format(DATE_FORMAT);
    },
    customEndDate: function () {
      return this.selectedRange?.end?.format(DATE_FORMAT);
    },
    customCompareStartDate: function () {
      return this.selectedCompareRange.start.format(DATE_FORMAT);
    },
    customCompareEndDate: function () {
      return this.selectedCompareRange.end.format(DATE_FORMAT);
    },
    minStartDate: function () {
      return (
        this.dateRangeMaxDateBack ??
        dayjs.utc().subtract(1, "year").format(DATE_FORMAT)
      );
    },
    maxStartDate: function () {
      return this.dateRangeMaxDate ?? null;
    },
    minCompareDate: function () {
      return dayjs.utc().subtract(2, "year").format(DATE_FORMAT);
    },
  },
  methods: {
    toDateRange: function ({ isRelative, start, end }) {
      return {
        start: getRelativeDate(isRelative, start),
        end: getRelativeDate(isRelative, end),
      };
    },
    getPrevPeriod: function ({ start, end }) {
      const daysDiff = end.diff(start, "day");
      return {
        start: start.subtract(daysDiff + 1, "day"),
        end: start.subtract(1, "day"),
      };
    },
    formatDateRangeLabel: function ({ start, end }) {
      if (!start) {
        return "";
      } else if (!end || start.isSame(end, "day")) {
        return `(${getFormattedDate(start)})`;
      } else {
        return `(${getFormattedDate(start)} - ${getFormattedDate(end)})`;
      }
    },
    initRange: function (value) {
      return value?.useDatePicker
        ? this.toDateRange({
            isRelative: false,
            start: this.customRange.start,
            end: this.customRange.end,
          })
        : this.toDateRange(value);
    },
    handleClick: function (item) {
      this.selectedItem = item.value;
      if (!item.value?.useDatePicker) {
        this.selectedRange = this.toDateRange(item.value);
      }
      this.selectedCompare = compareItems[0];
      this.selectedCompareRange = this.getPrevPeriod(this.selectedRange);
    },
    handleChangeCustomDates: function ({ start, end }) {
      if (start && end) {
        this.selectedRange = this.toDateRange({
          isRelative: false,
          start,
          end,
        });
        this.selectedCompareRange = this.getPrevPeriod(this.selectedRange);
      }
    },
    handleChangeCustomCompare: function ({ start, end }) {
      if (start && end) {
        this.selectedCompareRange = this.toDateRange({
          isRelative: false,
          start,
          end,
        });
      }
    },
    handleApply: function () {
      this.activeItem = this.selectedItem;
      this.activeRange = this.selectedRange;
      this.activeCompare = this.selectedCompare;
      this.activeCompareRange = this.selectedCompareRange;
      this.$refs.menu.save();
      this.emitChangeAction();
    },
    handleCancel: function () {
      this.selectedItem = this.activeItem;
      this.selectedRange = this.activeRange;
      this.selectedCompare = this.activeCompare;
      this.selectedCompareRange = this.activeCompareRange;
      this.$refs.menu.save();
    },
    handleReset: function () {
      const range = this.initRange(this.initialValue);
      this.activeItem = this.initialValue;
      this.selectedItem = this.initialValue;
      this.activeRange = range;
      this.selectedRange = range;
      this.activeCompare = compareItems[0];
      this.selectedCompare = compareItems[0];
      this.activeCompareRange = this.getPrevPeriod(range);
      this.selectedCompareRange = this.getPrevPeriod(range);
    },
    emitChangeAction: function () {
      this.$emit(EVENTS.CHANGE, {
        range: this.activeRange,
        activeItem: this.activeItem,
        ...(this.activeCompare?.useDatePicker && {
          compare: this.activeCompareRange,
        }),
      });
    },
  },
  watch: {
    value(val) {
      const range = this.initRange(val);
      this.selectedItem = this.activeItem = this.initialValue = val;
      this.activeRange = this.selectedRange = range;
    },
    customCompareRange(val) {
      if (val) {
        const { start, end } = val;
        this.selectedCompare = compareItems[1];
        this.selectedCompareRange = this.toDateRange({
          isRelative: false,
          start,
          end,
        });
      }
    },
  },
};
</script>

<style scoped>
.daterange-subtext {
  font-size: 0.75em;
  color: #aaaaaa;
}

.hyp-text-menu-btn {
  border-radius: unset;
  border-bottom: 1px solid #aaaaaa;
  margin-top: 13px;
}

.hyp-date-range-filters-container {
  background-color: white;
}
.hyp-text-menu-btn.full-width-menu {
  width: 100%;
  padding: 0 5px 0 0;
}
.hyp-text-menu-btn.full-width-menu .v-icon {
  margin-right: 0px !important;
  margin: auto;
}
</style>
